import { useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePoll } from "@/hooks/usePoll";
import { errMsg, get } from "@/lib/api";
import { fmt, fmtQty, fmtTime } from "@/lib/format";

const Side = ({ levels, side, max }) => (
  <div className="space-y-px">
    <div className={`grid grid-cols-3 px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 ${side === "sell" ? "text-right" : ""}`}>
      <span>{side === "buy" ? "Bid" : "Ask"}</span><span className="text-center">Orders</span><span className="text-right">Qty</span>
    </div>
    {levels.map((l, i) => (
      <div key={i} data-testid={`depth-${side}-${i}`} className="relative grid grid-cols-3 px-2 py-1 font-mono text-xs">
        <div className="absolute inset-y-0 right-0" style={{ width: `${(l.quantity / max) * 100}%`,
          background: side === "buy" ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)" }} />
        <span className={`relative ${side === "buy" ? "text-emerald-400" : "text-rose-400"}`}>{fmt(l.price)}</span>
        <span className="relative text-center text-slate-500">{l.orders}</span>
        <span className="relative text-right text-slate-200">{fmtQty(l.quantity)}</span>
      </div>
    ))}
  </div>
);

export const DepthPanel = ({ sid }) => {
  const [auto, setAuto] = useState(true);
  const { data, error, refresh } = usePoll(() => get(`/symbols/${sid}/depth`), auto ? 2000 : 0, [sid, auto]);
  if (error) return <div data-testid="depth-error" className="rounded border border-rose-900/60 bg-rose-950/30 p-3 text-xs text-rose-300">{errMsg(error)}</div>;
  if (!data) return <div className="flex h-32 items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-500" /></div>;
  const max = Math.max(1, ...data.buy.map((x) => x.quantity), ...data.sell.map((x) => x.quantity));
  return (
    <div data-testid="depth-panel" className="space-y-2">
      <div className="flex items-center justify-between text-[11px] font-mono text-slate-500">
        <span>Spread <span className="text-slate-200">{fmt(data.spread)}</span> · fetched {fmtTime(data.fetched_at)} · {data.source}</span>
        <div className="flex gap-1">
          <Button data-testid="depth-auto-toggle" size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => setAuto((a) => !a)}>
            {auto ? "AUTO 2s" : "PAUSED"}
          </Button>
          <Button data-testid="depth-refresh-button" size="icon" variant="ghost" className="h-6 w-6" onClick={refresh}><RefreshCw className="h-3 w-3" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 rounded border border-slate-800 bg-slate-950/50 p-1">
        <Side levels={data.buy} side="buy" max={max} />
        <Side levels={data.sell} side="sell" max={max} />
      </div>
      <div className="flex justify-between font-mono text-[11px] text-slate-500">
        <span>Total bid <span className="text-emerald-400">{fmtQty(data.total_bid_qty)}</span></span>
        <span>Total ask <span className="text-rose-400">{fmtQty(data.total_ask_qty)}</span></span>
      </div>
    </div>
  );
};
